import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    IconButton,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    ButtonBase,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import MapIcon from "@mui/icons-material/Map";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate } from "react-router-dom";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";

// Generic Card Component
const SettingsCard = ({ title, subtitle, icon, children, onClick }) => (
    <Paper
        sx={{
            p: 2,
            borderRadius: "16px",
            border: "1px solid #E0E0E0",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            cursor: onClick ? "pointer" : "default",
            "&:hover": { boxShadow: onClick ? 3 : 0 },
        }}
        elevation={0}
        onClick={onClick}
    >
        <Box display="flex" alignItems="center" gap={1} mb={subtitle ? 1.5 : 1.5}>
            {icon}
            <Typography variant="subtitle1" fontWeight={600}>
                {title}
            </Typography>
        </Box>
        {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {subtitle}
            </Typography>
        )}
        <Box width="100%">{children}</Box>
    </Paper>
);

// Google Map Key Card
function GoogleMapKeyCard() {
    const [apiKey, setApiKey] = useState(""); // actual key
    const [displayKey, setDisplayKey] = useState("********************"); // masked by default
    const [editMode, setEditMode] = useState(false);
    const [otpDialogOpen, setOtpDialogOpen] = useState(false);
    const [otp, setOtp] = useState("");
    const [mobileNum, setMobileNum] = useState("");
    const [actionType, setActionType] = useState(""); // "view" or "edit"
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    useEffect(() => {
        const userData = JSON.parse(localStorage.getItem("userData"));
        if (userData) setMobileNum(userData.mobile_num);
    }, []);

    const showSnackbar = (message, severity = "info") => setSnackbar({ open: true, message, severity });
    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const fetchKey = async () => {
        try {
            const res = await getData(API_ENDPOINTS.getAllCharges);
            if (res?.statusCode === 200 && res.message.data?.length > 0) {
                const keyObj = res.message.data.find((c) => c.key === "google_api_key");
                if (keyObj?.value_text) {
                    setApiKey(keyObj.value_text);
                    setDisplayKey(keyObj.value_text); // show actual key after OTP
                }
            }
        } catch (error) {
            showSnackbar("Failed to fetch Google API Key", "error");
        }
    };

    const handleOtpRequest = async (type) => {
        try {
            const res = await postData(API_ENDPOINTS.sendOtp, { mobile_num: mobileNum });
            if (res.status === "SUCCESS") {
                setOtpDialogOpen(true);
                setActionType(type);
                showSnackbar(`OTP sent successfully! OTP: ${res.result.otp}`, "success");
            } else {
                showSnackbar(res.message || "Failed to send OTP", "error");
            }
        } catch (error) {
            showSnackbar("Error sending OTP", "error");
        }
    };

    const handleOtpSubmit = async () => {
        try {
            const res = await postData(API_ENDPOINTS.verifyOtpLogin, { mobile_num: mobileNum, otp });
            if (res.status === "SUCCESS") {
                setOtpDialogOpen(false);
                setOtp("");

                // fetch actual key and show in input
                await fetchKey();

                if (actionType === "edit") setEditMode(true);
                showSnackbar("OTP Verification Successful!", "success");
            } else {
                showSnackbar(res.message || "OTP verification failed", "error");
            }
        } catch (error) {
            showSnackbar("Error verifying OTP", "error");
        }
    };

    const handleSave = async () => {
        try {
            const resAll = await getData(API_ENDPOINTS.getAllCharges);
            const charges = resAll?.statusCode === 200 ? resAll.message.data || [] : [];
            const existingKey = charges.find((c) => c.key === "google_api_key");

            let res;
            if (existingKey) {
                res = await postData(API_ENDPOINTS.updateCharge, { key: "google_api_key", value_text: apiKey });
            } else {
                res = await postData(API_ENDPOINTS.generateCharges, { key: "google_api_key", value_text: apiKey, value_numeric: 0 });
            }

            if (res.statusCode === 200) {
                showSnackbar("Google API Key saved successfully", "success");
                setEditMode(false);
                setDisplayKey("********************"); // mask after saving
            } else {
                showSnackbar("Failed to save Google API Key", "error");
            }
        } catch (error) {
            showSnackbar("Something went wrong!", "error");
        }
    };

    const maskMobileNumber = (number) => {
        if (!number) return "";
        const visibleDigits = 4;
        const maskedPart = "X".repeat(number.length - visibleDigits);
        return number.slice(0, visibleDigits) + maskedPart;
    };

    return (
        <>
            <SettingsCard
                title="Google Map Key Management"
                subtitle="Manage your Google Maps API keys"
                icon={<MapIcon sx={{ color: "error.main", fontSize: 20 }} />}
            >
                <Box display="flex" alignItems="flex-start" gap={1} width="100%">
                    <TextField
                        label="API Key"
                        value={displayKey}
                        onChange={(e) => { setDisplayKey(e.target.value); setApiKey(e.target.value); }}
                        size="small"
                        fullWidth
                        disabled={!editMode}
                    />
                    {!editMode ? (
                        <>
                            <IconButton color="primary" onClick={() => handleOtpRequest("view")}>
                                <VisibilityIcon />
                            </IconButton>
                            <IconButton color="secondary" onClick={() => handleOtpRequest("edit")}>
                                <EditIcon />
                            </IconButton>
                        </>
                    ) : (
                        <IconButton color="success" onClick={handleSave}>
                            <SaveIcon />
                        </IconButton>
                    )}
                </Box>
            </SettingsCard>

            {/* OTP Dialog */}
            <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)}>
                <DialogTitle>OTP Verification</DialogTitle>
                <DialogContent>
                    <Typography>
                        Please enter the OTP sent to your registered mobile: {maskMobileNumber(mobileNum)}
                    </Typography>
                    <TextField
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        fullWidth
                        sx={{ mt: 2 }}
                        placeholder="Enter OTP"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOtp(""); setOtpDialogOpen(false); }}>Cancel</Button>
                    <Button onClick={handleOtpSubmit} variant="contained">Verify</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </>
    );
}

// Main Settings Page
function Settings() {
    const navigate = useNavigate();

    return (
        <Box padding={2}>
            <h1>Admin Settings</h1>
            <p>Configure your system preferences and integrations here.</p>

            <Box
                mt={2}
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }}
                gap={2}
            >
                {/* Account Settings */}
                <SettingsCard
                    title="Account Settings"
                    icon={<PersonOutlineIcon sx={{ color: "success.main", fontSize: 20 }} />}
                >
                    <ButtonBase sx={{ mt: 1 }} onClick={() => navigate("/app/profile")}>
                        Update Profile
                    </ButtonBase>
                </SettingsCard>

                {/* Fee Structure & Taxation */}
                <SettingsCard
                    title="Fee Structure & Taxation"
                    subtitle="Detailed information on our Platform Fee and the mandated GST calculation."
                    icon={<RequestQuoteIcon sx={{ color: "primary.main", fontSize: 20 }} />}
                    onClick={() => navigate("/app/billing-settings")}
                />

                {/* Package Management System */}
                <SettingsCard
                    title="Package Management System"
                    subtitle="Centralized package distribution system"
                    icon={<RequestQuoteIcon sx={{ color: "warning.main", fontSize: 20 }} />}
                    onClick={() => navigate("/app/package-management")}
                />

                {/* Google Map Key Card */}
                <GoogleMapKeyCard />
            </Box>
        </Box>
    );
}

export default Settings;
